/**
 * Numerals and dates, one way each. Everything goes through Intl with the
 * browser's locale; digits are tabular where a CSS rule can make them so
 * (.stat__value, .table, .num). Rates take one decimal, counts none, money
 * two: pick the precision here, not at the call site.
 */

const DASH = '—';

export function formatNumber(n: number | null | undefined, digits = 0): string {
  if (n === null || n === undefined || Number.isNaN(n)) return DASH;
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(n);
}

/** `x` is already a percentage (42.5 → "42.5%"). */
export function formatPercent(x: number | null | undefined, digits = 1): string {
  if (x === null || x === undefined || Number.isNaN(x)) return DASH;
  return `${formatNumber(x, digits)}%`;
}

/** Minor units (cents) to a currency string: 4900, 'usd' → "$49.00". */
export function formatMoneyMinor(
  minor: number | null | undefined,
  currency = 'usd',
  digits = 2,
): string {
  if (minor === null || minor === undefined || Number.isNaN(minor)) return DASH;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(minor / 100);
}

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const d = parse(iso);
  return d
    ? d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : iso;
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const d = parse(iso);
  return d
    ? d.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : iso;
}

/**
 * "just now", "12 s ago", "3 min ago", "2 h ago", "4 d ago", "in 3 d". Render
 * inside <time dateTime={iso} title={formatDateTime(iso)}> so the exact
 * moment is one hover away.
 */
export function formatRelative(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return DASH;
  const d = parse(iso);
  if (!d) return iso;
  const s = Math.round((now - d.getTime()) / 1000);
  const abs = Math.abs(s);
  const past = s >= 0;
  const wrap = (text: string) => (past ? `${text} ago` : `in ${text}`);
  if (abs < 5) return 'just now';
  if (abs < 60) return wrap(`${abs} s`);
  const m = Math.round(abs / 60);
  if (m < 60) return wrap(`${m} min`);
  const h = Math.round(m / 60);
  if (h < 24) return wrap(`${h} h`);
  return wrap(`${Math.round(h / 24)} d`);
}

/** A uuid or other long id, shortened for a cell; put the full id in `title`. */
export function shortId(id: string): string {
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}
